export const REFRESH_AUTH_TOKEN_ENDPOINT = '/api/Auth/Refresh';

export const REFRESH_AUTH_TOKEN_TIMEOUT_MS = 10000;

/**
 * Set by Alps.Web.Authentication on a 401 caused by an expired JWT
 */
export const TOKEN_EXPIRED_HEADER = 'x-token-expired';

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Asks Yeti to exchange the refresh token cookie for a new ALPS_WEB_TOKEN cookie.
 * Concurrent callers share the same request, so a burst of 401s only triggers one refresh.
 * A refresh that doesn't answer within REFRESH_AUTH_TOKEN_TIMEOUT_MS counts as failed and is cancelled, so a hung
 * request can't leave every request waiting on it hanging too.
 * Uses window.fetch directly so a failed refresh can never trigger another refresh.
 * @returns true if the auth cookies were refreshed
 */
export const refreshAuthToken = (): Promise<boolean> => {
	if (!refreshInFlight) {
		refreshInFlight = fetchWithTimeout(REFRESH_AUTH_TOKEN_ENDPOINT, {
			method: 'POST',
			credentials: 'same-origin',
		})
			.then((response) => response.ok)
			.catch(() => false)
			.finally(() => {
				refreshInFlight = null;
			});
	}

	return refreshInFlight;
};

/**
 * window.fetch, but a request that doesn't answer within REFRESH_AUTH_TOKEN_TIMEOUT_MS is cancelled and rejects with
 * an AbortError. Racing the timeout means a hung request still fails even if aborting it doesn't settle it.
 */
export const fetchWithTimeout = (url: string, init: RequestInit): Promise<Response> => {
	const controller = new AbortController();
	let timeoutId: ReturnType<typeof setTimeout>;

	const timeout = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(() => {
			controller.abort();
			reject(new DOMException('Timed out', 'AbortError'));
		}, REFRESH_AUTH_TOKEN_TIMEOUT_MS);
	});

	return Promise.race([
		window.fetch(url, { ...init, signal: controller.signal }),
		timeout,
	]).finally(() => clearTimeout(timeoutId));
};
